import { useState, useEffect, useCallback } from 'react';
import type {
  Connection,
  Breakpoint,
  ElementTarget,
  ViewportTarget,
  TargetFn,
  CalculatedLine,
  Point,
} from './connections';
import {
  IN_VIEW_MARGIN,
  isTargetFn,
  isViewportTarget,
  isElementTarget,
  normalizeTarget,
  getAnchorPosition,
  measure,
  parseViewportValue,
} from './connections';

export function getCurrentBreakpoint(): Breakpoint {
  // Using Tailwind's md breakpoint (768px)
  return window.innerWidth >= 768 ? 'desktop' : 'mobile';
}

function calculatePosition(
  target: ElementTarget | ViewportTarget | TargetFn
): Point | null {
  if (isTargetFn(target)) {
    return target();
  }
  if (isViewportTarget(target)) {
    return {
      x: parseViewportValue(target.x, 'width'),
      y: parseViewportValue(target.y, 'height'),
    };
  }

  const element = document.querySelector(target.element);
  if (!element) {
    console.warn(`Element not found: ${target.element}`);
    return null;
  }

  const bounds = measure(element);
  const pos = getAnchorPosition(bounds, target.anchor);

  // Apply offset if provided
  if (target.offset) {
    pos.x += target.offset.x || 0;
    pos.y += target.offset.y || 0;
  }

  return pos;
}

/** Selector to watch for an in-view line: explicit, else the `to`/`from` element. */
function watchSelector(conn: Connection): string | undefined {
  if (conn.inView?.watch) return conn.inView.watch;
  if (isElementTarget(conn.to)) return conn.to.element;
  if (isElementTarget(conn.from)) return conn.from.element;
  if (typeof conn.to === 'string') return conn.to;
  if (typeof conn.from === 'string') return conn.from;
  return undefined;
}

function elementSelectors(conn: Connection): string[] {
  return [conn.from, ...(conn.via ?? []), conn.to].flatMap((t) => {
    const n = normalizeTarget(t);
    return isTargetFn(n) || isViewportTarget(n) ? [] : [n.element];
  });
}

export function useConnections(connections: Connection[]) {
  const [lines, setLines] = useState<CalculatedLine[]>([]);
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(getCurrentBreakpoint());

  const calculateLines = useCallback(() => {
    const currentBreakpoint = getCurrentBreakpoint();
    setBreakpoint(currentBreakpoint);

    const calculated: CalculatedLine[] = [];

    // measure() is viewport-relative, but the SVG lives at the top of the
    // document. Convert to document coordinates so scroll position
    // (e.g. restored scroll on refresh) doesn't shift the lines.
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    connections.forEach((conn, index) => {
      // Filter by breakpoint
      if (conn.breakpoints && !conn.breakpoints.includes(currentBreakpoint)) {
        return;
      }

      const targets = [conn.from, ...(conn.via ?? []), conn.to].map(normalizeTarget);
      const points: Point[] = [];
      for (const target of targets) {
        const pos = calculatePosition(target);
        if (!pos) return;
        points.push({ x: pos.x + scrollX, y: pos.y + scrollY });
      }

      const id = conn.id || `line-${index}`;
      const color = conn.style?.color || '#34639d';
      calculated.push({
        id,
        points,
        path: conn.path || 'straight',
        trigger: conn.trigger || 'load',
        style: {
          color,
          width: conn.style?.width || 2,
          opacity: conn.style?.opacity ?? 1,
          dashArray: conn.style?.dashArray,
          nodeColor: conn.style?.nodeColor || color,
          tipColor: conn.style?.tipColor || '#bfdbfe',
          glowColor: conn.style?.glowColor || '#60a5fa',
        },
        animation: {
          delay: conn.animation?.delay ?? index * 0.2,
          duration: conn.animation?.duration ?? 1.5,
          ease: conn.animation?.ease || 'easeInOut',
        },
        node: conn.node,
        inView: {
          watch: watchSelector(conn),
          margin: conn.inView?.margin || IN_VIEW_MARGIN,
        },
        scroll: { focus: conn.scroll?.focus ?? 0.7 },
        reveals: conn.reveals ?? conn.id,
      });
    });

    setLines(calculated);
  }, [connections]);

  useEffect(() => {
    // Coalesce bursts of observer callbacks into one measurement per frame
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        calculateLines();
      });
    };

    // Initial calculation
    calculateLines();

    // Recalculate on resize only (not scroll - let lines scroll naturally with content)
    window.addEventListener('resize', schedule);

    // Re-measure when any referenced element, or the page as a whole, changes size
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    const seen = new Set<string>();
    connections.forEach((conn) => {
      elementSelectors(conn).forEach((selector) => {
        if (seen.has(selector)) return;
        seen.add(selector);
        const el = document.querySelector(selector);
        if (el) observer.observe(el);
      });
    });

    // Web fonts swapping in shifts text metrics after first paint
    document.fonts?.ready.then(schedule).catch(() => {});

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [calculateLines, connections]);

  return { lines, breakpoint };
}
